/**
 * Centralized utilities for reading and writing CSS variables.
 * Provides a single source of truth for layout and other CSS variables.
 *
 * @example
 * // Set a layout variable
 * setCssVariable(LAYOUT_VARS.headerOffset, '80px');
 */

// Cached root styles reference. getComputedStyle() returns a live object.
/** @type {CSSStyleDeclaration|null} */
let cachedRootStyles = null;

/**
 * Reset the root styles cache. For testing purposes only.
 * @internal
 */
export function __resetCacheForTesting() {
    cachedRootStyles = null;
}

/**
 * Get computed styles for the document root using a persistent cache.
 * This avoids repeated getComputedStyle() calls during startup and hot paths.
 *
 * @returns {CSSStyleDeclaration} The cached computed styles
 */
function getRootStyles() {
    if (!cachedRootStyles) {
        cachedRootStyles = getComputedStyle(document.documentElement);
    }
    return cachedRootStyles;
}

/**
 * CSS custom property names for dynamic layout dimensions.
 * These are typically set by layoutVariables.js and consumed by CSS scroll-padding-top, etc.
 */
export const LAYOUT_VARS = {
    headerOffset: '--header-offset',
    announcementOffset: '--announcement-bar-offset',
    subNavigationOffset: '--sub-navigation-offset',
    stickyOffset: '--sticky-offset',
    scrollPadding: '--scroll-padding',
    bottomOffset: '--bottom-offset',
};

/**
 * Convert CSS values like "48rem" or "1200px" into numbers (pixels).
 * Handles rem, px, and unitless numeric values.
 *
 * @param {string} value - CSS value to convert (e.g., "16px", "2rem", "1280")
 * @param {number|null} rootFontSize - Optional root font size override for rem conversions.
 * @returns {number|null} Pixel value, or null if the value cannot be parsed
 *
 * @example
 * convertCssValueToPixels("16px")    // → 16
 * convertCssValueToPixels("2rem")    // → 32 (assuming 16px root font size)
 * convertCssValueToPixels("1280")    // → 1280
 * convertCssValueToPixels("invalid") // → null
 */
export function convertCssValueToPixels(value, rootFontSize = null) {
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) return null;

    if (trimmedValue.endsWith('rem')) {
        const numericValue = Number.parseFloat(trimmedValue);
        if (!Number.isFinite(numericValue)) return null;
        const computedRootFontSize = rootFontSize ?? getRootFontSize();
        return numericValue * computedRootFontSize;
    }

    if (trimmedValue.endsWith('px')) {
        const numericValue = Number.parseFloat(trimmedValue);
        return Number.isFinite(numericValue) ? numericValue : null;
    }

    // Handle unitless numeric values
    const numericValue = Number.parseFloat(trimmedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
}

/**
 * Read a CSS variable from :root and convert to pixels.
 * Falls back to provided value when the variable is missing or not parseable.
 *
 * @param {string} variableName - CSS variable name including the '--' prefix
 * @param {number} [fallback] - Value to return if variable is not found or cannot be parsed (defaults to 0)
 * @returns {number} The variable value in pixels, or the fallback
 *
 * @example
 * getCssVariableInPixels('--header-height', 80) // → 80 (if var not set)
 * getCssVariableInPixels('--header-height') // → 0 (if var not set, using default)
 * getCssVariableInPixels('--header-height', 80) // → 64 (if var is "64px")
 */
export function getCssVariableInPixels(variableName, fallback = 0) {
    const rootStyles = getRootStyles();
    const value = rootStyles.getPropertyValue(variableName);
    const pixels = convertCssValueToPixels(value);
    return Number.isFinite(pixels) ? pixels : fallback;
}

/**
 * Read a CSS variable value as a string (without conversion).
 * Useful for color values, font families, or other non-numeric CSS properties.
 *
 * @param {string} variableName - CSS variable name including the '--' prefix
 * @param {string} [fallback] - Value to return if variable is not found
 * @returns {string} The variable value, or the fallback
 *
 * @example
 * const brandColor = getCssVariable('--color-blue-500'); // → 'rgb(0 156 222)'
 * const bgColor = getCssVariable('--color-gray-50', '#f3f4f6');
 */
export function getCssVariable(variableName, fallback = '') {
    const rootStyles = getRootStyles();
    const value = rootStyles.getPropertyValue(variableName).trim();
    return value || fallback;
}

/**
 * Read a CSS variable that holds a color and return the color the browser paints.
 *
 * getCssVariable returns the raw computed value of a custom property, which leaves
 * light-dark() unresolved: the semantic tokens read back as
 * 'light-dark(rgb(0 156 222), rgb(0 156 222))', a string no chart or animation
 * library can parse. Assigning that value to a real color property makes the
 * browser resolve light-dark() against the color-scheme, so the computed style is
 * a plain color. Use this for every semantic color token; plain primitives such as
 * --color-gray-900 work with getCssVariable too.
 *
 * The probe carries the context's substituted value and its color-scheme, and then
 * lives on the document root rather than inside the context. A probe placed in the
 * context itself resolves nothing when the context is a shadow host: light-DOM
 * children of a host stay outside the flat tree unless a slot renders them, so they
 * inherit no custom property and the color reads back as black.
 *
 * @param {string} variableName - CSS variable name including the '--' prefix
 * @param {string} [fallback] - Value to return when the variable is not declared
 * @param {Element} [context] - Element whose scope holds the value and picks the light-dark() arm
 * @returns {string} A resolved color such as 'rgb(0, 156, 222)', or the fallback
 *
 * @example
 * getCssColor('--graphic-brand-default');            // → 'rgb(0, 156, 222)'
 * getCssColor('--surface-brand-default', '', panel); // resolved inside a scheme-dark section
 */
export function getCssColor(variableName, fallback = '', context = document.documentElement) {
    const contextStyles = getComputedStyle(context);
    const value = contextStyles.getPropertyValue(variableName).trim();

    if (value === '') {
        return fallback;
    }

    const probe = document.createElement('span');
    probe.style.display = 'none';
    probe.style.colorScheme = contextStyles.colorScheme;
    probe.style.color = value;
    document.documentElement.append(probe);
    const color = getComputedStyle(probe).color;
    probe.remove();

    return color;
}

/**
 * Set a CSS variable value.
 * By default, sets the variable on document root. Optionally target a specific element.
 *
 * @param {string} variableName - CSS variable name including the '--' prefix
 * @param {string} value - CSS value to set (e.g., "16px", "2rem", "#fff")
 * @param {HTMLElement} [target] - Element to set the variable on (defaults to document root)
 *
 * @example
 * setCssVariable('--header-height', '80px');
 *
 * @example
 * // Set on a specific element
 * const modal = document.querySelector('.modal');
 * setCssVariable('--modal-width', '600px', modal);
 */
export function setCssVariable(variableName, value, target = document.documentElement) {
    target.style.setProperty(variableName, value);
}

/**
 * Get the root (html) element font size in pixels.
 * Used for converting rem values to pixels. Falls back to 16px (browser default).
 *
 * @returns {number} Root font size in pixels, or 16 if unavailable
 *
 * @example
 * const rootSize = getRootFontSize(); // → 16 (typically)
 * const twoRemInPixels = 2 * rootSize; // → 32
 */
export function getRootFontSize() {
    const rootStyles = getRootStyles();
    const fontSize = Number.parseFloat(rootStyles.fontSize);
    return Number.isFinite(fontSize) ? fontSize : 16;
}
